import pages from '../site/metadata.json';

// Public GA4 web-stream identifier, not a credential.
export const GA_ID = 'G-K8YXNEK44S';
export const ANALYTICS_CHOICE_KEY = 'cubitplay.analytics.choice.v1';
const CHOICE_COOKIE = 'cubitplay_analytics';
const PUBLISHED_HOSTS = new Set(['cubitplay.com', 'www.cubitplay.com']);
const listeners = new Set();
const completed = new Set();
let memoryChoice = null;
let preferenceWriteFailed = false;
let initialized = false;
let observing = false;
let lastLocation = null;
let lastReferrer = '';

export const isPublishedSite = () => window.location.protocol === 'https:' &&
  PUBLISHED_HOSTS.has(window.location.hostname) && (!window.location.port || window.location.port === '443');
const validChoice = value => ['accepted', 'declined'].includes(value) ? value : null;
function readStoredChoice() {
  try {
    const cookie = document.cookie.split('; ').find(item => item.startsWith(CHOICE_COOKIE + '='));
    const choice = validChoice(cookie?.split('=')[1]);
    if (choice) return choice;
  } catch { /* Preferences remain usable for this visit. */ }
  try { return validChoice(localStorage.getItem(ANALYTICS_CHOICE_KEY)); }
  catch { /* Blocked/malformed preference cookie: fail closed (no analytics consent). */ return null; }
}
export const getAnalyticsChoice = () => memoryChoice || readStoredChoice();

function saveChoice(choice) {
  memoryChoice = choice;
  try { localStorage.setItem(ANALYTICS_CHOICE_KEY, choice); } catch { /* Cookie/in-memory fallback. */ }
  try {
    const domain = isPublishedSite() ? '; Domain=cubitplay.com' : '';
    document.cookie = `${CHOICE_COOKIE}=${choice}; Path=/; Max-Age=15552000; SameSite=Lax; Secure${domain}`;
  } catch { /* In-memory preference still takes effect immediately. */ }
  // A blocked write must not let an old stored Accept override a new Decline.
  preferenceWriteFailed = readStoredChoice() !== choice;
}

function removeGaCookies() {
  try {
    const names = document.cookie.split(';').map(item => item.trim().split('=')[0]);
    const domains = ['', window.location.hostname, '.' + window.location.hostname, 'cubitplay.com', '.cubitplay.com'];
    for (const name of names.filter(name => name === '_ga' || name.startsWith('_ga_'))) {
      for (const domain of domains) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure${domain ? '; Domain=' + domain : ''}`;
      }
    }
  } catch { /* Cannot erase data already sent or inaccessible cookies. */ }
}

const consent = analytics => ({ analytics_storage: analytics, ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
const safePath = path => Object.prototype.hasOwnProperty.call(pages, path) ? path : '/404';
function pageContext(path = window.location.pathname) {
  const clean = safePath(path);
  return { page_location: window.location.origin + clean, page_title: pages[clean].title };
}
function initialReferrer() {
  try {
    const url = new URL(document.referrer);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    // External referrers retain only origin; unknown/private paths never leave the app.
    return url.origin + (PUBLISHED_HOSTS.has(url.hostname) ? safePath(url.pathname) : '/');
  } catch { /* Reject malformed URLs rather than emitting identifying path fragments. */ return ''; }
}

export function initAnalytics() {
  if (!isPublishedSite()) return false;
  if (!observing) {
    observing = true;
    let previous = getAnalyticsChoice();
    const sync = () => {
      if (preferenceWriteFailed) return;
      const next = readStoredChoice();
      if (next === previous && next === memoryChoice) return;
      previous = next;
      memoryChoice = next;
      applyChoice(next);
      listeners.forEach(listener => listener(next));
    };
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('pageshow', sync);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') sync(); });
  }
  const disabled = getAnalyticsChoice() !== 'accepted';
  window[`ga-disable-${GA_ID}`] = disabled;
  if (disabled) { removeGaCookies(); return false; }
  if (initialized) return true;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  // Explicit opt-in: this initialization is reachable only after acceptance.
  // No tag, consent-mode ping, page view or gameplay event is sent beforehand.
  window.gtag('consent', 'default', consent('granted'));
  window.gtag('js', new Date());
  window.gtag('set', { ...pageContext(), page_referrer: initialReferrer(), allow_google_signals: false, allow_ad_personalization_signals: false });
  window.gtag('config', GA_ID, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false, cookie_path: '/', cookie_flags: 'SameSite=Lax;Secure' });
  if (!document.getElementById('cubitplay-ga-tag')) {
    const tag = document.createElement('script');
    tag.id = 'cubitplay-ga-tag';
    tag.async = true;
    tag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(tag);
  }
  return true;
}
const canTrack = () => isPublishedSite() && getAnalyticsChoice() === 'accepted' && initAnalytics();

function applyChoice(choice) {
  if (!isPublishedSite()) return;
  if (choice !== 'accepted') {
    window[`ga-disable-${GA_ID}`] = true;
    if (initialized) window.gtag('consent', 'update', consent('denied'));
    removeGaCookies();
    lastLocation = null;
  } else {
    window[`ga-disable-${GA_ID}`] = false;
    if (initAnalytics()) {
      window.gtag('consent', 'update', consent('granted'));
      trackPageView(window.location.pathname);
    }
  }
}
export function setAnalyticsChoice(choice) {
  if (!validChoice(choice)) return;
  saveChoice(choice);
  applyChoice(choice);
  listeners.forEach(listener => listener(choice));
}
export function subscribeAnalyticsChoice(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function trackPageView(path) {
  if (!canTrack()) return false;
  const context = pageContext(path);
  if (lastLocation === context.page_location) return false;
  lastReferrer = lastLocation || initialReferrer();
  lastLocation = context.page_location;
  window.gtag('set', { ...context, page_referrer: lastReferrer });
  window.gtag('event', 'page_view', { ...context, page_referrer: lastReferrer, send_to: GA_ID });
  return true;
}

// ---------------------------------------------------------------------------
// Reusable event architecture
//
// EVENT_NAMES is the canonical set of events all CubitPlay games may send.
// game_name identifies which game the event came from (default: perfect_slice).
// Future games call registerGame('my_game') to allow their game_name, then pass
// game_name in every event payload.
//
// Privacy: no PII (email, nickname, user ID, IP) is ever sent to GA.
// Deduplication: trackOnce uses sessionStorage + in-memory Set to guarantee
// each logical action produces exactly one event even in React StrictMode.
// ---------------------------------------------------------------------------

// Allowed game_name slugs. registerGame() extends this for future CubitPlay games.
const ALLOWED_GAME_NAMES = new Set(['perfect_slice']);

/**
 * Allow a new CubitPlay game to use the shared event taxonomy.
 * Call once at game initialisation, before any events are tracked.
 * name must be 2–40 lowercase letters, digits or underscores.
 */
export function registerGame(name) {
  if (typeof name === 'string' && /^[a-z0-9_]{2,40}$/.test(name)) ALLOWED_GAME_NAMES.add(name);
}

// ---------------------------------------------------------------------------
// Event name registry
// Legacy CubitPlay events kept for backward compatibility with custom dimensions
// and key events already configured in GA4.
// New events follow the GA4 games taxonomy (level_start / level_end / post_score)
// and CubitPlay-specific engagement events.
// ---------------------------------------------------------------------------
const EVENT_NAMES = new Set([
  // ── Legacy CubitPlay events (already in GA4 custom dimensions / key events) ──
  'game_start',           // Endless: session start; Daily: first cut (kept for back-compat)
  'round_complete',       // Endless: round finalised
  'daily_attempt_saved',  // Daily: individual cut accepted by server
  'daily_complete',       // Daily: all three cuts done
  // ── Standard GA4 auth events ──
  'sign_up',              // New account profile completed, including nickname
  'login',                // Existing confirmed account signs in
  // ── GA4 games taxonomy (Google-recommended) ──
  'level_start',          // Endless: each new round begins; Daily: first cut
  'level_end',            // Endless: round finalised; Daily: all three cuts done
  'post_score',           // Final best score recorded for a completed round/daily
  // ── CubitPlay Daily-specific ──
  'daily_start',          // Daily: player submits their very first cut for the day
  // ── Engagement ──
  'share',                // Player completes a share action (image save, text copy, native)
  'leaderboard_view',     // Player opens the Daily leaderboard
  'guest_claim',          // Anonymous guest history successfully merged into a registered account
  // ── Cross-game nudge funnel ──
  'nudge_shown',          // Cross-game nudge modal appeared on the results screen
  'nudge_cta_click',      // Player tapped the "Play [other game]" CTA inside the nudge
  'nudge_dismiss',        // Player dismissed the nudge via "Maybe later" or the ✕ button
]);

// ---------------------------------------------------------------------------
// Parameter builder
// Validates and sanitises all parameters before they reach GA.
// Callers only supply what they know; absent params are not emitted.
// ---------------------------------------------------------------------------
function eventParams(name, input) {
  // ── Standard GA4 auth events: only method. ──
  if (name === 'sign_up' || name === 'login') return { method: 'email' };

  // ── Guest claim: minimal context, no game state. ──
  if (name === 'guest_claim') {
    const p = {};
    if (typeof input.kept_existing === 'boolean') p.kept_existing = input.kept_existing;
    return p;
  }

  // ── Cross-game nudge funnel events ──
  if (name === 'nudge_shown' || name === 'nudge_cta_click' || name === 'nudge_dismiss') {
    const VALID_SLUGS = new Set([...ALLOWED_GAME_NAMES]);
    const p = {};
    if (typeof input.from_game === 'string' && VALID_SLUGS.has(input.from_game)) p.from_game = input.from_game;
    if (typeof input.to_game   === 'string' && VALID_SLUGS.has(input.to_game))   p.to_game   = input.to_game;
    if (typeof input.target_streak === 'number' && Number.isFinite(input.target_streak) && input.target_streak >= 0) {
      p.target_streak = Math.min(Math.floor(input.target_streak), 9999);
    }
    return p;
  }

  // ── All game / engagement events ──

  // game_name: accept caller-supplied slug for future CubitPlay games.
  // Falls back to 'perfect_slice' when absent or not yet registered.
  const gameName = typeof input.game_name === 'string' && ALLOWED_GAME_NAMES.has(input.game_name)
    ? input.game_name : 'perfect_slice';
  const params = { game_name: gameName };

  // game_mode: standardised values only — never free-form strings.
  if (['daily', 'endless'].includes(input.game_mode)) params.game_mode = input.game_mode;

  // Boolean flags.
  if (typeof input.logged_in === 'boolean') params.logged_in = input.logged_in;

  // Share method (for share events only; ignored by other events).
  if (['native', 'copy_text', 'download_image'].includes(input.share_method)) {
    params.share_method = input.share_method;
  }

  // Numeric parameters — each validated independently with explicit bounds.
  // Invalid values are silently dropped so a bad score never suppresses game_mode.
  // Existing parameter names are preserved for backward compat with custom definitions.
  const numericParams = [
    // Existing (GA4 custom dimensions already registered in property)
    ['round_number',       0,   1_000_000_000],
    ['attempt_count',      0,   3],
    ['score',              0,   1_000_000],
    ['error_percent',      0,   100],
    // New parameters for Google-recommended events and richer reporting
    ['attempt_number',     1,   3],      // 1–3; which attempt this event describes
    ['difference_percent', 0,   100],    // alias for error_percent (how far off the target)
    ['accuracy',           0,   100],    // complement: 100 - difference_percent (how close)
  ];

  for (const [key, min, max] of numericParams) {
    const v = input[key];
    if (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max) params[key] = v;
  }
  return params;
}

export function trackEvent(name, input = {}) {
  if (!EVENT_NAMES.has(name) || !canTrack()) return false;
  trackPageView(window.location.pathname);
  window.gtag('event', name, { ...eventParams(name, input), ...pageContext(), page_referrer: lastReferrer, send_to: GA_ID });
  return true;
}

/**
 * Fires an event exactly once per logical action, regardless of React StrictMode
 * double-invocation or network retries.
 *
 * internalKey must uniquely identify this action instance (e.g. a round UUID,
 * request UUID, or challenge ID). It is NEVER sent to Google Analytics —
 * it is used only as a local sessionStorage deduplication key.
 */
export function trackOnce(name, internalKey, input = {}) {
  if (!canTrack() || !EVENT_NAMES.has(name)) return false;
  const key = `cubitplay.ga.once:${name}:${internalKey}`;
  if (completed.has(key)) return false;
  try { if (sessionStorage.getItem(key)) return false; } catch { /* Memory fallback. */ }
  if (!trackEvent(name, input)) return false;
  completed.add(key);
  try { sessionStorage.setItem(key, '1'); } catch { /* Memory fallback. */ }
  return true;
}

/**
 * Alias for trackOnce — the idiomatic entry point for game event tracking.
 * Future CubitPlay games use this with their own game_name registered via registerGame().
 *
 * Example:
 *   registerGame('word_wave');
 *   trackGameEvent('level_start', roundId, { game_name: 'word_wave', game_mode: 'daily', logged_in: true });
 */
export { trackOnce as trackGameEvent };
